import { useIdentity } from '@vp/auth-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api-config';
import { IApiHooksWithTrigger, IIdentityObject } from '../../interfaces/app-interfaces';
import {
  clearChatbotToken,
  clearIsSessionExpired,
  getChatbotToken,
  getIsSessionExpired,
  getRestId,
  setChatbotToken,
  setChatSessionType,
  setIsSessionExpired,
  setIsUserLoggedInAtChatStart,
} from '../../utils/session-storage-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { IMessage } from '../../interfaces/component-interfaces';
import { handleGetMessageList } from '../../utils/chatbot/order-tracking/response-handlers';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { CAContext } from '../../contexts/CAContext';
import { getChatbotSessionInfo } from '../../utils/chatbot/api-utils';
import { isFeatureEnabled } from '@vp/ab-reader';

export interface IChatSessionData {
  messages: IMessage[];
  discussionId: string;
  sessionId: string;
}

export const useCBChatRetrieve = (): IApiHooksWithTrigger<IChatSessionData> => {
  const {
    chatState: { chatSession, locale },
    setChatState,
  } = useContext(CAContext);

  const { setState: setChatbotState } = useContext(ChatbotContext);
  const { logCustomAttributesNR } = useNRLogging();

  const [data, setData] = useState<IChatSessionData>();
  const identity = useIdentity();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);

  const trigger = useCallback(() => {
    setShouldFetch(true);
  }, []);

  /** Clear chatbot session data if new session starts */
  useEffect(() => {
    if (chatSession === 'initial') {
      setData(undefined);
      setShouldFetch(false);
      setLoading(false);
      setError(undefined);
    }
  }, [chatSession]);

  useEffect(() => {
    const fetchData = async () => {
      if (shouldFetch) {
        setLoading(true);

        const chatbotToken = getChatbotToken();
        const sessionInfo = getChatbotSessionInfo(chatbotToken || undefined);
        const { authorizationHeader, canonicalId, isSignedIn } = identity as IIdentityObject;

        if (!chatbotToken) {
          const error = new Error('Chatbot token not found');
          error.name = 'ApiError';
          setError(error);
          setLoading(false);
          setShouldFetch(false);
          logCustomAttributesNR('ca-chatbot-api-error', {
            errorName: error.name,
            message: error.message,
            apiType: 'getChatbotMessages',
            logLevel: 'error',
          });
          setChatSessionType('initial');
          return;
        }
        if (sessionInfo.sessionId) {
          /** Check if session is expired */
          const isExpired = getIsSessionExpired(sessionInfo.sessionId) === 'expired';
          if (isExpired) {
            /** for chatbot session, reset the state to initial and return */
            if (chatSession === 'chatbotSession') {
              setChatState((prev) => ({
                ...prev,
                chatSession: 'initial',
                isPrivacyPolicyAccepted: false,
                unreadMessages: 0,
                endChatInfo: {
                  sessionType: 'chatbot',
                  sessionId: getChatbotSessionInfo(chatbotToken || undefined)?.sessionId || '',
                },
              }));
              setChatbotState((prev) => ({ ...prev, conversationMessages: [], discussionId: '', sessionId: '', showErrorMessage: false }));
              clearChatbotToken();
              setChatSessionType('initial');
            }
            /** For other (miaw) cases, return and do not call the API */
            clearIsSessionExpired(sessionInfo.sessionId);
            return;
          }
        }
        try {
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');
          myHeaders.append('Chatbot-Authorization', `Bearer ${chatbotToken}`);

          const requestId = getRestId() || '';
          const shopperId = canonicalId || '';

          const requestOptions = {
            method: 'GET',
            headers: myHeaders,
            redirect: 'follow' as RequestRedirect,
          };

          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Retrieving chatbot session data',
            apiType: 'getChatbotMessages',
          });

          // Construct URL with query parameters
          const url = new URL(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/get-messages`);
          url.searchParams.append('requestid', requestId);
          url.searchParams.append('shopperid', shopperId);

          fetch(url.toString(), requestOptions)
            .then((response) => {
              if (!response.ok) {
                if (response.status === 401) {
                  /** If session is expired, set the flag in session storage */
                  if (sessionInfo.sessionId) {
                    setIsSessionExpired(sessionInfo.sessionId, 'expired');
                  }
                  if (chatSession === 'chatbotSession') {
                    setChatbotState((prev) => ({ ...prev, isSessionExpired: true }));
                    /** Set endchatinfo as chatbot so that we can show the feedback */
                    setChatState((prev) => ({
                      ...prev,
                      endChatInfo: {
                        sessionType: 'chatbot',
                        sessionId: getChatbotSessionInfo(chatbotToken || undefined)?.sessionId || '',
                      },
                    }));
                  }
                }
                // If response is not ok (status not in the range 200-299),
                // throw an error to skip the next then block
                const error = new Error(`API responded with status: ${response.status}`);
                error.name = 'ApiError';
                throw error;
              }
              return response.json();
            })
            .then((result) => {
              logCustomAttributesNR('ca-chatbot-api-response', {
                logLevel: 'info',
                message: 'Retrieving chatbot session data success',
                apiType: 'getChatbotMessages',
              });
              console.log('Session retrieve result:', result);
              /** If refreshed_token is present, update the chatbot token */
              if (result?.refreshed_token) {
                setChatbotToken(result.refreshed_token, isSignedIn);
              }
              setIsUserLoggedInAtChatStart(isSignedIn);
              const transformedData = handleGetMessageList(locale, result, getChatbotToken() || '');
              console.log('Transformed session data:', transformedData);
              setData(transformedData);
              setChatState((prevState) => ({
                ...prevState,
                isProductAgentEnabled: isFeatureEnabled('chatbot-product-agent'),
              }));

              if (transformedData.messages.length === 1) {
                setChatbotState((prevState) => ({ showHelperButton: true, ...prevState }));
              }
              setError(undefined);
            })
            .catch((error) => {
              setError(error as Error);
              console.error(error);
              logCustomAttributesNR('ca-chatbot-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                apiType: 'getChatbotMessages',
                logLevel: 'error',
              });
            })
            .finally(() => {
              setLoading(false);
              setShouldFetch(false);
            });
        } catch (err) {
          setError(err as Error);
          setLoading(false);
          setShouldFetch(false);
        }
      }
    };
    fetchData();
  }, [shouldFetch]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};
